<template>
  <div class="container my-5">
    <div class="cards-grid">
      <div
        v-for="(cell, idx) in cells"
        :key="idx"
        class="grid-item"
      >
        <!-- Mittleres Feld (null) → zentrale Karte -->
        <div
          v-if="cell === null"
          class="card-center text-center p-4 m-4 glass-container"
        >
          <h4 class="center-card-title  m-0">TV Anwendungsbereiche</h4>
        </div>

        <!-- Alle anderen Karten (nur wenn cell wirklich ein Objekt ist) -->
        <div
          v-else-if="cell"
          class="card usage-card position-relative d-flex flex-column"
        >
          <div class="card-body d-flex flex-column">
            <h5 class="card-title text-start">
              <i :class="cell.icon + ' fs-3 me-2'"></i><br>
              {{ cell.title }}
            </h5>
            <ul class="mb-0 text-start flex-grow-1">
              <li
                v-for="(item, i) in cell.items"
                :key="i"
                :class="{ 'mb-2': i < cell.items.length - 1 }"
              >
                <i class="bi bi-check-circle me-2"></i>{{ item }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Fallback für unerwartetes undefined -->
        <div v-else class="empty-cell"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const cards = [
  {
    title: 'Heimkino & Filme (dunkler Raum)',
    icon: 'bi bi-tv',
    items: [
      'Kontrastkönner: OLED (WOLED/QD-OLED) oder starkes FALD/Mini-LED',
      'HDR-Formate: Dolby Vision/HDR10(+) sinnvoll',
      'Bewegung: gute Zwischenbildberechnung für ruhige Kameraschwenks',
      'Größe & Nähe: groß fürs Feeling z. B. 65–83 Zoll ab ~2,5 m Sitzabstand',
      'Spiegelungen: entspiegelte Oberflächen optional, weniger kritisch im Dunkeln'
    ]
  },
  {
    title: 'Helles Wohnzimmer & Familie',
    icon: 'bi bi-brightness-high',
    items: [
      'Helligkeit: hohe Spitzen- und Dauerhelligkeit und eher Mini-LED/QD-OLED',
      'Entspiegelung: starke Anti-Reflex-Beschichtung',
      'Blickwinkel: Ist gerade bei Sofas übers Eck relevant im Wohnzimmer',
      'Smart-OS: App-Auswahl & schnelle Bedienung (Google TV, webOS, Tizen)',
      'Praktisch im Alltag: Wandhalterung spart Platz und ist ergonomischer für die Kids'
    ]
  },
  {
    title: 'Sport & Live-TV',
    icon: 'bi bi-broadcast',
    items: [
      'Schärfe in Bewegung: besser bis zu 144 Hz-Panel',
      'Tuner & PVR: DVB-T2/C/S2, Timeshift & CI+ je nach Bedarf',
      'Upscaling: gute Hochskalierung von HD auf 4K – leider bietet die Quelle meist nicht mehr als HD',
    ]
  },
  {
    title: 'Gaming: Konsole & PC',
    icon: 'bi bi-controller',
    items: [
      'HDMI 2.1 für Konsolen: 4K120, VRR (G-Sync/FreeSync)',
      'Input-Lag: niedrig für kompetetive Games',
      'Panelwahl: OLED für Top-Reaktionszeit & Kontrast, Mini-LED für Helligkeit',
      'PC-Nutzung: 42–48 Zoll als „Desk-TV“',
    ]
  },
  {
    title: 'Streaming-Allround & Familie',
    icon: 'bi bi-collection-play',
    items: [
      'Einfach gut: 55–65 Zoll, 4K, solides 60–120 Hz-Panel',
      'Apps: Netflix, Prime, Disney+, YouTube, Mediatheken, AirPlay/Chromecast',
      'Bedienung: flotte Menüs, brauchbare Fernbedienung/Sprachsuche',
    ]
  },
  {
    title: 'Preisbewusst & Einsteiger',
    icon: 'bi bi-cash-coin',
    items: [
      'Viel Bild fürs Geld: 40–65 Zoll und möglichst 4K',
      'Panel: 60 Hz ausreichend, zufriedenstellende Bewegungsdarstellung',
      'HDR-Basis: HDR10/HLG; Dolby Vision optional',
      'Apps & Bedienung: stabile Smart-Plattform mit den wichtigsten Diensten',
    ]
  },
  {
    title: 'Kleine Räume & Schlafzimmer',
    icon: 'bi bi-door-closed',
    items: [
      'Größe: 32–50 Zoll, abhängig vom Sitzabstand (z. B. 2 m → max. 43")',
      'Blickwinkel: IPS oder OLED sinnvoll bei seitlicher Sicht',
      'Ton: ggf. integrierte Soundlösung oder Soundbar mit Nachtmodus',
      'Bedienung: schnelles Einschalten, einfache Navigation',
      'Schlafmodus & Timer: wichtig für automatisches Abschalten'
  ]
},
  {
    title: 'Fernsehen für Kinder & Jugendliche',
    icon: 'bi bi-emoji-smile',
    items: [
      'Kindersicherung: Profile, PIN-Schutz & altersgerechte Inhalte',
      'YouTube & Mediatheken: Zugriff auf kindgerechte Apps notwendig',
      'Einfache Bedienung: große Icons, einfache Menüs',
      'Robustheit: stabiler Standfuß, sichere Montage',
      'Automatische Abschaltung: z. B. nach 60 Minuten Inaktivität'
  ]
}
];

// 3×3-Matrix mit null in der Mitte
const grid = computed(() => [
  [ cards[0], cards[1], cards[2] ],
  [ cards[3], null,      cards[4] ],
  [ cards[5], cards[6], cards[7] ]
])

const cells = computed(() => grid.value.flat())
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
@import 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.13.1/font/bootstrap-icons.css';

/* Default: Desktop ab 992px → 3 Spalten */
.cards-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
}

/* Tablet: 576px bis 991.98px → 2 Spalten */
@media (min-width: 576px) and (max-width: 991.98px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Smartphone: bis 575.98px → 1 Spalte, zentrale Karte ausblenden */
@media (max-width: 575.98px) {
  .cards-grid {
    grid-template-columns: 1fr;
    display: block;
  }
  .card-center {
    display: none !important;
  }
}

/* Gemeinsame Styles für alle Viewports */
.grid-item {
  display: flex;
}
.grid-item > .usage-card,
.grid-item > .card-center {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* 1) Karte: nur noch Gradient, kein Glass mehr */
.card-center {
  /* entferne Glass-Stile */
  background: linear-gradient(211deg, #60a5fa 0%, #0c53c6de 100%);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: none;
  box-shadow: none;

  /* behalte deine bisherigen Dimensionen */
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 1rem;  /* oder dein p-4 */
  margin: 1rem;   /* oder dein m-4 */
  position: relative;
}

/* 2) Glass-Overlay jetzt nur auf den Titel anwenden */
.center-card-title {
  position: relative;
  display: inline-block;       /* damit das Blur nur diesen Bereich betrifft */
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  /* Textfarbe optional dunkler wählen für besseren Kontrast:
     color: #111; */
}

/* 3) Optional: Hover-Effekt auf dem Text */
.center-card-title:hover {
  background: rgba(255,255,255,0.25);
  border-color: rgba(255,255,255,0.3);
  transform: translateY(-2px);
}

.usage-card {
  background-color: #F9FAFB;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease-in-out;
}
.usage-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: #3d84f6;
  margin-bottom: 1rem;
}

ul {
  list-style: none;
  padding-left: 0;
}
li {
  font-size: 0.875rem;
  color: #4B5563;
  line-height: 1.4;
}
.empty-cell {
  flex: 1;
}


</style>


